// Окно «Поддержка» (PRI-135) и панель «Обращения» администратора (PRI-136):
// подписи, даты, вкладки и группировка. Всё чистое — состояние и запросы в
// SupportWindow, здесь только правила, чтобы их можно было проверить без DOM.
// Подписи и порядок — как в эталоне (overlays/Support, logic/common.ts).

import type { SupportKind, SupportStatus, SupportTicketRow } from "@/api/client";
import { plural } from "@/lib/period";

/** Типы обращения в порядке формы нового обращения. */
export const SUPPORT_KINDS: { value: SupportKind; label: string }[] = [
  { value: "question", label: "Вопрос" },
  { value: "error", label: "Ошибка" },
  { value: "wish", label: "Пожелание" },
  { value: "access", label: "Доступ" },
];

export function kindLabel(kind: string): string {
  return SUPPORT_KINDS.find((k) => k.value === kind)?.label ?? kind;
}

// ---- Список автора: вкладки «Актуальные / Закрытые / Все» ----

export type UserTab = "open" | "closed" | "all";

export const USER_TABS: { value: UserTab; label: string }[] = [
  { value: "open", label: "Актуальные" },
  { value: "closed", label: "Закрытые" },
  { value: "all", label: "Все" },
];

export function inUserTab(row: Pick<SupportTicketRow, "is_open">, tab: UserTab): boolean {
  return tab === "all" || (tab === "open") === row.is_open;
}

/** Числа на вкладках считаются по всем обращениям, а не по показанным. */
export function userTabCounts(rows: Pick<SupportTicketRow, "is_open">[]): Record<UserTab, number> {
  return {
    open: rows.filter((r) => r.is_open).length,
    closed: rows.filter((r) => !r.is_open).length,
    all: rows.length,
  };
}

/** Число непрочитанных ответов по списку — для точки, пока ответ сервера не пришёл. */
export function unreadTotal(rows: Pick<SupportTicketRow, "unread">[]): number {
  return rows.reduce((a, r) => a + r.unread, 0);
}

/** «Ошибка · открыто · Токен hh.ru истёк» — вторая строка обращения автора. */
export function userRowSub(row: SupportTicketRow): string {
  return [kindLabel(row.kind), row.is_open ? "открыто" : "закрыто", row.preview].join(" · ");
}

/** «Марина Котова · Сеть «Вкусно и быстро» · Ошибка» — вторая строка обращения в
 *  панели администратора. Компания показывается, только если их в базе больше
 *  одной: иначе это одно и то же слово в каждой строке. */
export function adminRowSub(row: SupportTicketRow, withCompany: boolean): string {
  const parts = [row.user_name];
  if (withCompany && row.company) parts.push(row.company);
  parts.push(kindLabel(row.kind));
  return parts.join(" · ");
}

// ---- Даты ----

const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const pad = (n: number) => String(n).padStart(2, "0");

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dayDiff(d: Date, now: Date): number {
  const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((b - a) / 86_400_000);
}

/** «сегодня», «вчера», «9 окт»; другой год — «9 окт 2025». */
export function fmtTicketDate(iso: string | null | undefined, now: Date = new Date()): string {
  const d = parse(iso);
  if (!d) return "";
  const diff = dayDiff(d, now);
  if (diff === 0) return "сегодня";
  if (diff === 1) return "вчера";
  const year = d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : "";
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}${year}`;
}

/** Время реплики: «09:12» сегодня, иначе «9 окт, 09:12». */
export function fmtMessageTime(iso: string | null | undefined, now: Date = new Date()): string {
  const d = parse(iso);
  if (!d) return "";
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return dayDiff(d, now) === 0 ? hm : `${fmtTicketDate(iso, now)}, ${hm}`;
}

// ---- Окно администратора ----

export type AdminStatus = SupportStatus | "all";
export type AdminMode = "tickets" | "users" | "companies";

/** Кнопка статуса идёт по кругу: ждут ответа → ждут пользователя → закрытые → все. */
export function nextStatus(s: AdminStatus): AdminStatus {
  return ({ need: "wait", wait: "closed", closed: "all", all: "need" } as const)[s];
}

/** Кнопка режима идёт по кругу: обращения → пользователи → компании. Компаний
 *  в базе одна — режим «компании» тогда пропускается, группировать нечего. */
export function nextMode(m: AdminMode, hasCompanies: boolean): AdminMode {
  if (m === "tickets") return "users";
  if (m === "users") return hasCompanies ? "companies" : "tickets";
  return "tickets";
}

export const STATUS_TITLE: Record<AdminStatus, string> = {
  need: "Ждут ответа",
  wait: "Ждут пользователя",
  closed: "Закрытые",
  all: "Все обращения",
};

export const MODE_TITLE: Record<AdminMode, string> = {
  tickets: "Обращения — нажмите, чтобы сгруппировать по пользователям",
  users: "Пользователи — нажмите, чтобы сгруппировать по компаниям",
  companies: "Компании — нажмите, чтобы вернуться к обращениям",
};

/** Заголовок кнопки статуса: «Ждут ответа · 3 — нажмите, чтобы переключить». */
export function statusButtonTitle(s: AdminStatus, count: number): string {
  return `${STATUS_TITLE[s]} · ${count} — нажмите, чтобы переключить`;
}

/** Заголовок кнопки режима; у единственной компании подсказка про неё не нужна. */
export function modeButtonTitle(m: AdminMode, hasCompanies: boolean): string {
  if (m === "users" && !hasCompanies) return "Пользователи — нажмите, чтобы вернуться к обращениям";
  return MODE_TITLE[m];
}

/** «ЭО» из «Элина Осколкова»; в названии компании кавычки и № не считаются. */
export function initials(name: string): string {
  return name
    .replace(/[«»"№]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

export interface AdminGroup {
  /** Идентификатор для перехода внутрь: id автора или название компании. */
  key: string;
  name: string;
  sub: string;
  /** Сколько обращений в группе. */
  count: number;
  /** Из них ждут ответа. */
  need: number;
  isCompany: boolean;
}

function groupBy(
  rows: SupportTicketRow[],
  keyOf: (r: SupportTicketRow) => string,
  make: (key: string, list: SupportTicketRow[]) => AdminGroup,
): AdminGroup[] {
  const map = new Map<string, SupportTicketRow[]>();
  for (const r of rows) {
    const k = keyOf(r);
    const list = map.get(k);
    if (list) list.push(r);
    else map.set(k, [r]);
  }
  // Rows приходят от сервера свежими сверху: группы встают в порядке своей
  // самой свежей строки — Map помнит порядок первой вставки.
  return [...map.entries()].map(([k, list]) => make(k, list));
}

const needIn = (list: SupportTicketRow[]) => list.filter((r) => r.status === "need").length;

/** Режим «пользователи»: один человек — одна группа, под ним компания. */
export function groupByUser(rows: SupportTicketRow[]): AdminGroup[] {
  return groupBy(
    rows,
    (r) => r.user_id,
    (key, list) => ({
      key,
      name: list[0]!.user_name,
      sub: list[0]!.company ?? "",
      count: list.length,
      need: needIn(list),
      isCompany: false,
    }),
  );
}

/** Режим «компании»: под названием — сколько в ней пользователей. */
export function groupByCompany(rows: SupportTicketRow[]): AdminGroup[] {
  return groupBy(
    rows,
    (r) => r.company ?? "—",
    (key, list) => {
      const users = new Set(list.map((r) => r.user_id)).size;
      return {
        key,
        name: key,
        sub: `${users} ${plural(users, "пользователь", "пользователя", "пользователей")}`,
        count: list.length,
        need: needIn(list),
        isCompany: true,
      };
    },
  );
}

/** Показывать список плоско или группами. Внутрь группы попадают, выбрав в ней
 *  пользователя или компанию. */
export function isFlat(mode: AdminMode, userId: string | null, company: string | null): boolean {
  return mode === "tickets" || (mode === "companies" && !!company) || (mode === "users" && !!userId);
}

// ---- Форма нового обращения ----

export function canCreate(title: string, text: string): boolean {
  return title.trim() !== "" && text.trim() !== "";
}

/** Картинка для переписки: тип и размер проверяем до отправки, чтобы ошибка была
 *  понятной, а не сетевой. Сервер держит те же границы. */
export const SUPPORT_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
export const SUPPORT_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

export function imageProblem(file: { type: string; size: number }): string | null {
  if (!SUPPORT_IMAGE_TYPES.includes(file.type)) return "Можно прикрепить картинку: PNG, JPEG, WebP или GIF.";
  if (file.size > SUPPORT_IMAGE_MAX_BYTES) return "Картинка больше 8 МБ.";
  return null;
}

/** Текст ошибки отправки для человека. */
export function sendProblem(status: number | undefined): string {
  if (status === 409) return "Обращение уже закрыто, писать в него нельзя.";
  if (status === 413) return "Картинка слишком большая.";
  if (status === 415) return "Этот тип файла не подходит: нужна картинка.";
  return "Не удалось отправить. Попробуйте ещё раз.";
}
